<script setup lang="ts">
import type { ProductHeaders } from '~/models/ProductHeaders'
import type { FiltersQueryParams } from '~/models/Filter'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { waitFor } from '~/utils/asyncUtils'
import arrayUtils from '~/utils/arrayUtils'
import useSubjectsStore from '~/stores/subjects'
import useProductStore from '~/stores/product'
import useFilterSettingsStore from '~/stores/filterSettings'
import useFilterStore from '~/stores/filter'
import { QueryParamKey } from '~/models/QueryParamKeys'
import { MaxTimeoutMs } from '~/constants/api'
import useQueryParams from '~/composables/queryParams'
import Button from '~/components/ui/Button.vue'
import UpcomingSubjectButton from '~/components/ResourceFinder/UpcomingSubjectButton.vue'
import TaskTypeFilter from '~/components/ResourceFinder/TaskTypeFilter.vue'
import SubjectFilter from '~/components/ResourceFinder/SubjectFilter.vue'
import ProductFilter from '~/components/ResourceFinder/ProductFilter.vue'
import LegacyHeaderFilter from '~/components/ResourceFinder/LegacyHeaderFilter.vue'
import LegacyGradeFilter from '~/components/ResourceFinder/LegacyGradeFilter.vue'
import ColophonButton from '~/components/ResourceFinder/ColophonButton.vue'

const props = withDefaults(defineProps<{
  autoSelect?: boolean
  enableSubjectFilter?: boolean
  enableGradeFilter?: boolean
  headerKey?: keyof ProductHeaders
}>(), {
  autoSelect: false,
  enableSubjectFilter: true,
  enableGradeFilter: true,
  headerKey: 'learningPath'
})

const { t } = useI18n()
const filterStore = useFilterStore()
const {
  selectedHeaderChildren,
  selectedHeaders,
} = storeToRefs(filterStore)
const { resetViewMode } = filterStore
const { filterOptions } = storeToRefs(useFilterSettingsStore())
const { clear } = useQueryParams<FiltersQueryParams>(QueryParamKey.Filters)
const { hasLoaded: hasLoadedProducts } = storeToRefs(useProductStore())
const { getRelatedLocationsByGradeAndSubject } = useProductStore()
const { truthy } = arrayUtils()

const { currentSubject } = storeToRefs(useSubjectsStore())
const { selectedGrade } = storeToRefs(useFilterStore())

const clearFilter = () => {
  resetViewMode()
  clear()
}

const hasUpcomingContent = ref(false)

const isArchivedContent = computed(() => props.headerKey === 'archivedContent')

watch([selectedGrade, currentSubject], async () => {
  await waitFor(() => hasLoadedProducts.value, MaxTimeoutMs)
  if (!(selectedGrade.value && currentSubject.value))
    return
  const { upcomingLocationIds } = await getRelatedLocationsByGradeAndSubject(selectedGrade.value, currentSubject.value)
  hasUpcomingContent.value = upcomingLocationIds.filter(truthy).length > 0
}, { immediate: true })
</script>

<template>
  <header
    id="resource-finder"
    class="max-w-screen-au relative mx-auto my-8 flex flex-col flex-wrap gap-4 px-4 transition-all sm:flex-row sm:items-center sm:px-8"
  >
    <SubjectFilter v-if="enableSubjectFilter && filterOptions.subject" />
    <LegacyGradeFilter v-if="enableGradeFilter && filterOptions.grade" />
    <ProductFilter v-if="filterOptions.product" />
    <LegacyHeaderFilter
      v-if="filterOptions.header"
      :index="0"
      :enable-grade-filter="enableGradeFilter"
      :header-key
    />
    <LegacyHeaderFilter
      v-for="(headerChildren, index) in selectedHeaderChildren"
      :key="headerChildren[0]?.locationId"
      :index="index + 1"
      :resources="headerChildren"
      :auto-select="props.autoSelect"
      :enable-grade-filter="enableGradeFilter"
      :header-key
    />
    <TaskTypeFilter v-if="filterOptions.taskType" />
    <Button
      v-if="selectedHeaders.length > 0"
      variant="ghost"
      size="lg"
      @click="clearFilter"
    >
      {{ t('filters.emptyFilter') }}
    </Button>

    <div class="ml-auto grid auto-cols-max grid-flow-col gap-4">
      <UpcomingSubjectButton
        v-if="hasUpcomingContent && currentSubject"
        :subject-code="currentSubject"
        :legacy="true"
      />
      <Button
        v-else-if="isArchivedContent && currentSubject"
        :to="{ name: 'subject', params: { subject: currentSubject.toLowerCase() } }"
        class="relative"
      >
        {{ t('filters.toNewSubject') }}
      </Button>

      <ColophonButton v-if="hasLoadedProducts" />
    </div>
  </header>
</template>
